import { useLanguageStore } from '@/helpers/composables/useStores';
import {
	GetSingleQtvSimilarTitlesDocument,
	GetSingleQtvSimilarTitlesQuery,
	GetSingleQtvSimilarTitlesQueryVariables,
} from '@/components/buybox-new/qualityTV/queries/GetSingleQTV.query';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { computed } from 'vue';
import { SimilarTitlesSorting } from '@/@types/graphql-types';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { useQuery } from '@/helpers/composables/useApollo';

const singleQTVBannerAndDisclaimer: Record<string, Record<string, { bannerText: string; disclaimerText: string }>> = {
	GB: {
		dnp: {
			bannerText: 'Stream a similar title on Disney+ now for only £1.99/mo *',
			disclaimerText:
				'Offer applies to Standard with Ads. Offer ends 30/06/2025. New and eligible returning subscribers only. Must be 18+. After 4 months auto renews at the then-current Standard with Ads price of £4.99 a month unless you cancel before then.',
		},
	},
	ES: {
		dnp: {
			bannerText: 'Ve un título similar en Disney+ ahora por sólo 1,99 €/mes *',
			disclaimerText:
				'Oferta válida hasta el 30/06/25. Solo para nuevos suscriptores y antiguos suscriptores que reúnan los requisitos. Deben ser mayores de 18 años. Después de los 4 meses, tu suscripción se renovará automáticamente al precio mensual del plan Estándar con anuncios vigente en ese momento (actualmente 5,99 €), a menos que la canceles antes.',
		},
	},
};

export function useSingleQTV(
	title: MaybeRefOrGetter<TitleDetail>,
	providerShortName: MaybeRefOrGetter<string>,
	enabled: MaybeRefOrGetter<boolean>
) {
	const { country, language } = useLanguageStore();
	const _title = toValue(title);
	const variables = computed<GetSingleQtvSimilarTitlesQueryVariables>(() => ({
		country: country.value,
		language: language.value,
		first: 1,
		titleId: _title.__typename === 'Season' ? _title.show.id : _title.id,
		filters: {
			packages: [toValue(providerShortName)],
		},
		offerFilters: {
			packages: [toValue(providerShortName)],
			bestOnly: true,
		},
		sorting: SimilarTitlesSorting['ImdbScore'],
	}));

	const { result, loading, onError } = useQuery<
		GetSingleQtvSimilarTitlesQuery,
		GetSingleQtvSimilarTitlesQueryVariables
	>(GetSingleQtvSimilarTitlesDocument, variables, { enabled: toValue(enabled) });

	onError(error => {
		captureMessageForSentry('[Graphql GetSingleQtvSimilarTitlesQuery]', {
			where: '[SingleQTV.vue]',
			error,
		});
	});

	const sqtvBannerAndDisclaimer = computed(() => {
		const provider = toValue(providerShortName);

		return singleQTVBannerAndDisclaimer[country.value]?.[provider] ?? null;
	});

	const similarTitle = computed(() =>
		result.value?.node?.__typename === 'Movie' || result.value?.node?.__typename === 'Show'
			? result?.value?.node?.similarTitlesV2.edges?.[0]
			: null
	);

	const isSQTVEmpty = computed(() => {
		if (loading.value) return false;

		return similarTitle.value == null;
	});

	return {
		similarTitle,
		loading,
		isSQTVEmpty,
		sqtvBannerAndDisclaimer,
	};
}
